import { useEffect, useRef, useState } from 'react';
import createDOMPurify from 'dompurify';
import { cn } from '../lib/utils';

const FRAME_HELPER_STYLE = `
  :root {
    color-scheme: light;
  }
  html, body {
    margin: 0;
    padding: 0;
  }
  body {
    overflow-wrap: anywhere;
  }
  body[data-email-content-frame-fragment="true"] {
    background: white;
    color: rgb(17 24 39);
    line-height: 1.6;
    padding: 20px;
  }
  img {
    max-width: 100%;
    height: auto;
  }
  table {
    max-width: 100%;
  }
  pre {
    white-space: pre-wrap;
  }
`;

const EMAIL_EXTRA_TAGS = [
  'article',
  'base',
  'body',
  'center',
  'col',
  'colgroup',
  'footer',
  'head',
  'header',
  'html',
  'meta',
  'section',
  'style',
  'title',
];

const EMAIL_EXTRA_ATTRIBUTES = [
  'align',
  'background',
  'bgcolor',
  'border',
  'cellpadding',
  'cellspacing',
  'class',
  'color',
  'dir',
  'height',
  'id',
  'lang',
  'rel',
  'role',
  'style',
  'target',
  'title',
  'valign',
  'width',
  'xmlns',
  'xmlns:o',
  'xmlns:v',
];

const EMAIL_FORBIDDEN_TAGS = [
  'audio',
  'canvas',
  'embed',
  'fencedframe',
  'form',
  'frame',
  'frameset',
  'iframe',
  'input',
  'object',
  'option',
  'portal',
  'select',
  'script',
  'source',
  'textarea',
  'video',
];

const EMAIL_URI_PATTERN = /^(?:(?:https?|mailto|tel|cid|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/iu;
const FRAME_DEFAULT_MIN_HEIGHT = 240;
const RENDERABLE_CONTENT_SELECTOR = 'a, img, table, svg';
const HELPER_STYLE_SELECTOR = 'style[data-email-content-frame="helpers"]';
const QUOTED_REPLY_SELECTOR = [
  '.gmail_attr',
  '.gmail_quote',
  '.moz-cite-prefix',
  '.protonmail_quote',
  '.yahoo_quoted',
  'blockquote[type="cite"]',
  'div[type="cite"]',
].join(', ');
const ON_WROTE_PATTERN = /^>?[\s\u00a0]*On\s.+\swrote:\s*$/iu;
const FORWARDED_ORIGINAL_MESSAGE_PATTERN =
  /^>?[\s\u00a0]*-{2,}\s*(?:Original Message|Forwarded message)\s*-{2,}\s*$/iu;
const HEADER_LINE_PATTERNS = {
  from: /^>?[\s\u00a0]*From:\s.+$/iu,
  subject: /^>?[\s\u00a0]*Subject:\s.+$/iu,
  date: /^>?[\s\u00a0]*(?:Date|Sent):\s.+$/iu,
};

type BrowserView = Window & typeof globalThis;

let cachedPurifierWindow: BrowserView | null = null;
let cachedPurifier: ReturnType<typeof createDOMPurify> | null = null;

function getPurifier(view: BrowserView) {
  if (!cachedPurifier || cachedPurifierWindow !== view) {
    cachedPurifier = createDOMPurify(view);
    cachedPurifierWindow = view;
  }

  return cachedPurifier;
}

function getBrowserWindow() {
  return typeof window === 'undefined' ? null : window;
}

function stripLeadingHtmlPreamble(value: string) {
  let candidate = value.trimStart();

  if (candidate.charCodeAt(0) === 0xfeff) {
    candidate = candidate.slice(1).trimStart();
  }

  while (candidate.startsWith('<!--')) {
    const commentEnd = candidate.indexOf('-->');
    if (commentEnd < 0) {
      break;
    }
    candidate = candidate.slice(commentEnd + 3).trimStart();
  }

  if (candidate.startsWith('<?xml')) {
    const xmlEnd = candidate.indexOf('?>');
    if (xmlEnd >= 0) {
      candidate = candidate.slice(xmlEnd + 2).trimStart();
    }
  }

  return candidate;
}

function looksLikeHtmlDocument(value: string) {
  const candidate = stripLeadingHtmlPreamble(value).slice(0, 32).toLowerCase();
  return (
    candidate.startsWith('<!doctype') ||
    candidate.startsWith('<html') ||
    candidate.startsWith('<body')
  );
}

function sanitizeEmailHtml(html: string, view: BrowserView) {
  return getPurifier(view).sanitize(html, {
    WHOLE_DOCUMENT: true,
    FORCE_BODY: true,
    USE_PROFILES: { html: true },
    ADD_TAGS: EMAIL_EXTRA_TAGS,
    ADD_ATTR: EMAIL_EXTRA_ATTRIBUTES,
    ADD_DATA_URI_TAGS: ['img'],
    ALLOW_DATA_ATTR: true,
    ALLOWED_URI_REGEXP: EMAIL_URI_PATTERN,
    FORBID_TAGS: EMAIL_FORBIDDEN_TAGS,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  });
}

function parseHtmlDocument(html: string, view: BrowserView) {
  return new view.DOMParser().parseFromString(html, 'text/html');
}

function createBlankEmailDocument(view: BrowserView) {
  const doc = view.document.implementation.createHTMLDocument('');
  doc.documentElement.setAttribute('lang', 'en');
  return doc;
}

function cloneDocumentChildren(source: Element, target: Element) {
  target.replaceChildren(...Array.from(source.childNodes).map((node) => node.cloneNode(true)));
}

function buildRenderableEmailDocument(html: string, view: BrowserView) {
  const sanitizedMarkup = sanitizeEmailHtml(html, view);
  const sanitizedDocument = parseHtmlDocument(sanitizedMarkup, view);

  if (looksLikeHtmlDocument(html)) {
    return sanitizedDocument;
  }

  const fragmentDocument = createBlankEmailDocument(view);
  cloneDocumentChildren(sanitizedDocument.head, fragmentDocument.head);
  cloneDocumentChildren(sanitizedDocument.body, fragmentDocument.body);
  fragmentDocument.body.setAttribute('data-email-content-frame-fragment', 'true');
  return fragmentDocument;
}

function ensureHeadMeta(doc: Document) {
  if (!doc.head.querySelector('meta[charset]')) {
    const charsetMeta = doc.createElement('meta');
    charsetMeta.setAttribute('charset', 'utf-8');
    doc.head.prepend(charsetMeta);
  }

  if (!doc.head.querySelector('meta[name="viewport"]')) {
    const viewportMeta = doc.createElement('meta');
    viewportMeta.setAttribute('name', 'viewport');
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1');
    doc.head.append(viewportMeta);
  }
}

function hoistBodyStyles(doc: Document) {
  const knownStyles = new Set(
    Array.from(doc.head.querySelectorAll('style'))
      .map((styleTag) => styleTag.textContent?.trim() ?? '')
      .filter((value) => value.length > 0),
  );

  for (const styleTag of Array.from(doc.body.querySelectorAll('style'))) {
    const cssText = styleTag.textContent?.trim() ?? '';
    if (cssText.length === 0 || knownStyles.has(cssText)) {
      styleTag.remove();
      continue;
    }

    knownStyles.add(cssText);
    doc.head.appendChild(styleTag.cloneNode(true));
    styleTag.remove();
  }
}

function ensureBaseTarget(doc: Document) {
  const [firstBase, ...extraBases] = Array.from(doc.querySelectorAll('base'));
  for (const extraBase of extraBases) {
    extraBase.remove();
  }

  const baseTag = firstBase ?? doc.createElement('base');
  if (!baseTag.getAttribute('target')) {
    baseTag.setAttribute('target', '_blank');
  }

  if (baseTag.parentElement !== doc.head) {
    doc.head.prepend(baseTag);
  }
}

function ensureHelperStyle(doc: Document) {
  const existingHelperStyle = doc.head.querySelector(HELPER_STYLE_SELECTOR);
  if (existingHelperStyle) {
    existingHelperStyle.textContent = FRAME_HELPER_STYLE;
    return;
  }

  const helperStyle = doc.createElement('style');
  helperStyle.setAttribute('data-email-content-frame', 'helpers');
  helperStyle.textContent = FRAME_HELPER_STYLE;
  doc.head.append(helperStyle);
}

function normalizeEmailDocument(doc: Document) {
  if (!doc.documentElement.getAttribute('lang')) {
    doc.documentElement.setAttribute('lang', 'en');
  }

  if (!doc.body) {
    doc.documentElement.append(doc.createElement('body'));
  }

  ensureHeadMeta(doc);
  hoistBodyStyles(doc);
  ensureBaseTarget(doc);
  ensureHelperStyle(doc);

  return doc;
}

function hasRenderableHtmlContent(doc: Document) {
  if (doc.body.querySelector(RENDERABLE_CONTENT_SELECTOR)) {
    return true;
  }

  const visibleText = (doc.body.textContent ?? '').replace(/\u00a0/gu, ' ').trim();
  return visibleText.length > 0;
}

function serializeEmailDocument(doc: Document) {
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

function normalizeWhitespace(value: string) {
  return value
    .replace(/\u00a0/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
}

function normalizePlainText(text: string | null | undefined) {
  return text?.replace(/\r\n?/gu, '\n') ?? null;
}

function hasRenderableNodeContent(node: ParentNode | DocumentFragment) {
  if ('querySelector' in node && typeof node.querySelector === 'function') {
    if (node.querySelector(RENDERABLE_CONTENT_SELECTOR)) {
      return true;
    }
  }

  const visibleText = (node.textContent ?? '').replace(/\u00a0/gu, ' ').trim();
  return visibleText.length > 0;
}

function getPlainTextLineStartIndexes(lines: string[]) {
  const indexes: number[] = [];
  let offset = 0;

  for (const line of lines) {
    indexes.push(offset);
    offset += line.length + 1;
  }

  return indexes;
}

function looksLikePlainTextHeaderBlock(lines: string[], startIndex: number) {
  const block = lines
    .slice(startIndex, startIndex + 6)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return (
    block.some((line) => HEADER_LINE_PATTERNS.from.test(line)) &&
    block.some((line) => HEADER_LINE_PATTERNS.subject.test(line)) &&
    block.some((line) => HEADER_LINE_PATTERNS.date.test(line))
  );
}

function getCollapsedQuotedReplyText(text: string | null | undefined) {
  const normalizedText = normalizePlainText(text);
  if (!normalizedText?.trim()) {
    return null;
  }

  const lines = normalizedText.split('\n');
  const lineStartIndexes = getPlainTextLineStartIndexes(lines);
  let sawPrimaryContent = false;

  for (let index = 0; index < lines.length; index += 1) {
    const trimmedLine = lines[index]?.trim() ?? '';

    if (!sawPrimaryContent && trimmedLine.length > 0 && !trimmedLine.startsWith('>')) {
      sawPrimaryContent = true;
    }

    if (!sawPrimaryContent || trimmedLine.length === 0) {
      continue;
    }

    const startsQuotedReply =
      trimmedLine.startsWith('>') ||
      ON_WROTE_PATTERN.test(trimmedLine) ||
      FORWARDED_ORIGINAL_MESSAGE_PATTERN.test(trimmedLine) ||
      (HEADER_LINE_PATTERNS.from.test(trimmedLine) && looksLikePlainTextHeaderBlock(lines, index));

    if (!startsQuotedReply) {
      continue;
    }

    const collapsedText = normalizedText.slice(0, lineStartIndexes[index]).trimEnd();
    return collapsedText.trim().length > 0 ? collapsedText : null;
  }

  return null;
}

function getNodePath(node: Node, root: Node) {
  const path: number[] = [];
  let current: Node | null = node;

  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) {
      return null;
    }

    const index = Array.prototype.indexOf.call(parent.childNodes, current) as number;
    if (index < 0) {
      return null;
    }

    path.unshift(index);
    current = parent;
  }

  return current === root ? path : null;
}

function resolveNodePath(root: Node, path: number[]) {
  let current: Node | null = root;

  for (const index of path) {
    current = current?.childNodes.item(index) ?? null;
    if (!current) {
      return null;
    }
  }

  return current;
}

function hasQuotedReplyFollowingSibling(element: Element) {
  let sibling = element.nextElementSibling;

  while (sibling) {
    if (sibling.matches(QUOTED_REPLY_SELECTOR) || sibling.tagName === 'BLOCKQUOTE') {
      return true;
    }

    if (normalizeWhitespace(sibling.textContent ?? '').length > 0) {
      return false;
    }

    sibling = sibling.nextElementSibling;
  }

  return false;
}

function looksLikeHtmlHeaderBlock(element: Element) {
  const siblings = Array.from(element.parentElement?.children ?? []);
  const blockSource = siblings.length > 0 ? siblings : [element];
  const startIndex = blockSource.indexOf(element);
  if (startIndex < 0) {
    return false;
  }

  const block = blockSource
    .slice(startIndex, startIndex + 6)
    .map((node) => normalizeWhitespace(node.textContent ?? ''))
    .filter((line) => line.length > 0);

  return (
    block.some((line) => HEADER_LINE_PATTERNS.from.test(line)) &&
    block.some((line) => HEADER_LINE_PATTERNS.subject.test(line)) &&
    block.some((line) => HEADER_LINE_PATTERNS.date.test(line))
  );
}

function findQuotedReplyBoundary(doc: Document) {
  for (const element of Array.from(doc.body.querySelectorAll('*'))) {
    const textContent = normalizeWhitespace(element.textContent ?? '');

    if (element.matches(QUOTED_REPLY_SELECTOR)) {
      return element;
    }

    if (
      (ON_WROTE_PATTERN.test(textContent) ||
        FORWARDED_ORIGINAL_MESSAGE_PATTERN.test(textContent)) &&
      hasQuotedReplyFollowingSibling(element)
    ) {
      return element;
    }

    if (HEADER_LINE_PATTERNS.from.test(textContent) && looksLikeHtmlHeaderBlock(element)) {
      return element;
    }
  }

  return null;
}

function hasRenderableHtmlContentBeforeBoundary(doc: Document, boundary: Node) {
  if (!doc.body.contains(boundary)) {
    return false;
  }

  const range = doc.createRange();
  range.setStart(doc.body, 0);
  range.setEndBefore(boundary);

  return hasRenderableNodeContent(range.cloneContents());
}

function removeBoundaryAndFollowingSiblings(boundary: Node) {
  let node: ChildNode | null = boundary as ChildNode;

  while (node) {
    const nextSibling: ChildNode | null = node.nextSibling;
    node.remove();
    node = nextSibling;
  }
}

function getCollapsedQuotedReplyHtml(doc: Document) {
  const boundary = findQuotedReplyBoundary(doc);
  if (!boundary || !hasRenderableHtmlContentBeforeBoundary(doc, boundary)) {
    return null;
  }

  const path = getNodePath(boundary, doc.body);
  if (!path) {
    return null;
  }

  const collapsedDocument = doc.cloneNode(true) as Document;
  const collapsedBoundary = resolveNodePath(collapsedDocument.body, path);
  if (!collapsedBoundary) {
    return null;
  }

  removeBoundaryAndFollowingSiblings(collapsedBoundary);
  return hasRenderableHtmlContent(collapsedDocument)
    ? serializeEmailDocument(collapsedDocument)
    : null;
}

function buildEmailHtmlRenderState(
  html: string | null | undefined,
  collapseQuotedReplies: boolean,
) {
  const trimmed = html?.trim();
  if (!trimmed) {
    return {
      normalizedHtml: null,
      collapsedHtml: null,
    };
  }

  const view = getBrowserWindow();
  if (!view) {
    return {
      normalizedHtml: null,
      collapsedHtml: null,
    };
  }

  const normalizedDocument = normalizeEmailDocument(buildRenderableEmailDocument(trimmed, view));
  if (!hasRenderableHtmlContent(normalizedDocument)) {
    return {
      normalizedHtml: null,
      collapsedHtml: null,
    };
  }

  return {
    normalizedHtml: serializeEmailDocument(normalizedDocument),
    collapsedHtml: collapseQuotedReplies ? getCollapsedQuotedReplyHtml(normalizedDocument) : null,
  };
}

export function normalizeEmailHtml(html: string | null | undefined) {
  return buildEmailHtmlRenderState(html, false).normalizedHtml;
}

export type EmailContentFrameProps = {
  html?: string | null;
  text?: string | null;
  title: string;
  className?: string;
  viewportClassName?: string;
  viewMode?: 'auto' | 'formatted' | 'plain';
  quotedThreadCollapse?: {
    expandLabel: string;
    collapseLabel: string;
  };
};

function measureIframeHeight(frame: HTMLIFrameElement | null) {
  if (!frame) {
    return null;
  }

  try {
    const doc = frame.contentDocument;
    if (!doc) {
      return null;
    }

    const bodyHeight = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.body?.offsetHeight ?? 0,
      doc.body?.clientHeight ?? 0,
      Math.ceil(doc.body?.getBoundingClientRect().height ?? 0),
    );
    const documentHeight = Math.max(
      doc.documentElement?.scrollHeight ?? 0,
      doc.documentElement?.offsetHeight ?? 0,
      doc.documentElement?.clientHeight ?? 0,
      Math.ceil(doc.documentElement?.getBoundingClientRect().height ?? 0),
    );
    const nextHeight = Math.max(bodyHeight, documentHeight);

    return nextHeight > 0 ? nextHeight : null;
  } catch {
    return null;
  }
}

function scheduleFrameMeasurement(callback: () => void) {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
    return;
  }

  setTimeout(callback, 0);
}

export function EmailContentFrame({
  html,
  text,
  title,
  className,
  viewportClassName,
  viewMode = 'auto',
  quotedThreadCollapse,
}: EmailContentFrameProps) {
  const { normalizedHtml, collapsedHtml } = buildEmailHtmlRenderState(
    html,
    Boolean(quotedThreadCollapse),
  );
  const normalizedText = normalizePlainText(text);
  const collapsedText = quotedThreadCollapse ? getCollapsedQuotedReplyText(normalizedText) : null;
  const [quotedThreadExpanded, setQuotedThreadExpanded] = useState(false);
  const renderedHtml =
    quotedThreadCollapse && !quotedThreadExpanded
      ? (collapsedHtml ?? normalizedHtml)
      : normalizedHtml;
  const renderedText =
    quotedThreadCollapse && !quotedThreadExpanded
      ? (collapsedText ?? normalizedText)
      : normalizedText;
  const shouldRenderFormatted = viewMode !== 'plain' && Boolean(renderedHtml);
  const canToggleQuotedThread = shouldRenderFormatted
    ? Boolean(collapsedHtml)
    : Boolean(collapsedText);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [frameHeight, setFrameHeight] = useState<number | null>(null);

  useEffect(() => {
    setQuotedThreadExpanded(false);
  }, [html, text, title]);

  useEffect(() => {
    if (!shouldRenderFormatted || !renderedHtml) {
      setFrameHeight(null);
      return;
    }

    const frame = iframeRef.current;
    if (!frame) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    const imageListenerCleanup = new Map<HTMLImageElement, () => void>();

    const updateHeight = () => {
      const nextHeight = measureIframeHeight(frame);
      if (nextHeight) {
        setFrameHeight(Math.max(nextHeight, FRAME_DEFAULT_MIN_HEIGHT));
      }
    };

    const scheduleUpdate = () => {
      scheduleFrameMeasurement(updateHeight);
    };

    const bindImageListeners = (doc: Document) => {
      for (const image of Array.from(doc.images)) {
        if (imageListenerCleanup.has(image)) {
          continue;
        }

        const handleImageEvent = () => {
          scheduleUpdate();
        };

        image.addEventListener('load', handleImageEvent);
        image.addEventListener('error', handleImageEvent);
        imageListenerCleanup.set(image, () => {
          image.removeEventListener('load', handleImageEvent);
          image.removeEventListener('error', handleImageEvent);
        });
      }
    };

    const bindObservers = () => {
      const doc = frame.contentDocument;
      if (!doc) {
        return;
      }

      scheduleUpdate();
      bindImageListeners(doc);

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver?.disconnect();
        resizeObserver = new ResizeObserver(() => {
          scheduleUpdate();
        });

        if (doc.body) {
          resizeObserver.observe(doc.body);
        }
        if (doc.documentElement) {
          resizeObserver.observe(doc.documentElement);
        }
      }

      if (typeof MutationObserver !== 'undefined') {
        mutationObserver?.disconnect();
        mutationObserver = new MutationObserver(() => {
          bindImageListeners(doc);
          scheduleUpdate();
        });

        mutationObserver.observe(doc.documentElement, {
          subtree: true,
          childList: true,
          attributes: true,
          characterData: true,
        });
      }
    };

    frame.addEventListener('load', bindObservers);
    bindObservers();

    return () => {
      frame.removeEventListener('load', bindObservers);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      for (const cleanup of imageListenerCleanup.values()) {
        cleanup();
      }
      imageListenerCleanup.clear();
    };
  }, [renderedHtml, shouldRenderFormatted]);

  if (shouldRenderFormatted && renderedHtml) {
    return (
      <div
        className={cn(
          'overflow-hidden rounded-2xl border border-border bg-white shadow-soft',
          className,
        )}
      >
        <iframe
          ref={iframeRef}
          title={title}
          srcDoc={renderedHtml}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          scrolling="no"
          style={frameHeight ? { height: `${frameHeight}px` } : undefined}
          className={cn('w-full bg-white', viewportClassName)}
        />
        {quotedThreadCollapse && canToggleQuotedThread ? (
          <div className="border-t border-border/60 bg-white px-6 py-4">
            <button
              type="button"
              className="inline-flex cursor-pointer items-center rounded-sm text-sm font-medium text-primary underline decoration-current underline-offset-4 transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
              onClick={() => setQuotedThreadExpanded((current) => !current)}
            >
              {quotedThreadExpanded
                ? quotedThreadCollapse.collapseLabel
                : quotedThreadCollapse.expandLabel}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  if (!renderedText?.trim()) {
    return null;
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-border bg-background shadow-soft',
        className,
      )}
    >
      <div className={cn('bg-background px-6 py-6', viewportClassName)}>
        <p className="whitespace-pre-wrap text-sm leading-7 text-foreground">{renderedText}</p>
      </div>
      {quotedThreadCollapse && canToggleQuotedThread ? (
        <div className="border-t border-border/60 bg-background px-6 py-4">
          <button
            type="button"
            className="inline-flex cursor-pointer items-center rounded-sm text-sm font-medium text-primary underline decoration-current underline-offset-4 transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
            onClick={() => setQuotedThreadExpanded((current) => !current)}
          >
            {quotedThreadExpanded
              ? quotedThreadCollapse.collapseLabel
              : quotedThreadCollapse.expandLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
